import React, { useEffect, useState, useCallback } from 'react';
import Button from './Button';
import Icon from './Icons';

let listener = null;

/**
 * Imperative confirm dialog. Returns a Promise<boolean>.
 * Usage: const ok = await confirmDialog({ title, message, tone: 'danger' });
 * Requires <ConfirmDialogHost /> mounted once (see App.jsx).
 */
export const confirmDialog = (options = {}) =>
  new Promise((resolve) => {
    if (!listener) { resolve(window.confirm(options.message || '')); return; }
    listener({ ...options, resolve });
  });

export const ConfirmDialogHost = () => {
  const [state, setState] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    listener = (opts) => { setState(opts); requestAnimationFrame(() => setVisible(true)); };
    return () => { listener = null; };
  }, []);

  const close = useCallback((result) => {
    setVisible(false);
    setState((s) => { s?.resolve(result); return s; });
    setTimeout(() => setState(null), 200);
  }, []);

  useEffect(() => {
    if (!state) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') close(false);
      if (e.key === 'Enter') close(true);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [state, close]);

  if (!state) return null;

  const {
    title = 'Подтвердите действие',
    message,
    confirmText = 'Подтвердить',
    cancelText = 'Отмена',
    tone = 'primary',
    icon = tone === 'danger' ? 'alertCircle' : 'info',
  } = state;

  return (
    <div className="fixed inset-0 z-modal flex items-end justify-center p-4 sm:items-center" role="dialog" aria-modal="true" aria-label={title}>
      <div
        className={`absolute inset-0 bg-surface-0/70 backdrop-blur-sm transition-opacity duration-200 ${visible ? 'opacity-100' : 'opacity-0'}`}
        onClick={() => close(false)}
        aria-hidden="true"
      />
      <div
        className={`
          relative w-full max-w-sm surface-raised rounded-2xl p-5
          transition-all duration-200 ease-spring
          ${visible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-4 scale-95'}
        `.replace(/\s+/g, ' ').trim()}
      >
        <div className="flex items-start gap-3">
          <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-full ${tone === 'danger' ? 'bg-error-muted text-error' : 'bg-accent-muted text-accent'}`}>
            <Icon name={icon} size={20} />
          </span>
          <div className="min-w-0 flex-1">
            <h3 className="text-base font-semibold text-text-primary">{title}</h3>
            {message && <p className="mt-1 text-sm leading-6 text-text-secondary">{message}</p>}
          </div>
        </div>
        <div className="mt-5 flex gap-2">
          <Button variant="secondary" size="md" className="flex-1" onClick={() => close(false)}>
            {cancelText}
          </Button>
          <Button variant={tone === 'danger' ? 'danger' : 'primary'} size="md" className="flex-1" onClick={() => close(true)}>
            {confirmText}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDialogHost;
